import { v4 as uuidv4 } from 'uuid';
import { notifyStatusChange, notifyApprovalNeeded } from './notificationService';

// API base URL
const API_BASE = process.env.NODE_ENV === 'production' 
  ? 'https://apps.acom.edu.au/api'
  : 'http://localhost:5000/api';

// Request status types
export const RequestStatus = {
  PENDING: 'pending',
  IN_PROGRESS: 'in_progress',
  APPROVED: 'approved',
  REJECTED: 'rejected',
  COMPLETED: 'completed',
  HOLD: 'hold',
};

// Fetch wrapper with error handling
async function fetchAPI(endpoint, options = {}) {
  try {
    const response = await fetch(`${API_BASE}${endpoint}`, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
    });
    
    if (!response.ok) {
      throw new Error(`API request failed: ${response.statusText}`);
    }
    
    return await response.json();
  } catch (error) {
    console.error('API Error:', error);
    throw error;
  }
}

// Get all requests for current user
export const getMyRequests = async (userEmail) => {
  try {
    const allRequests = await fetchAPI('/requests');
    return allRequests.filter(request => 
      request.submittedBy === userEmail || 
      request.submittedBy === 'current.user@acom.edu.au'
    );
  } catch (error) {
    console.error('Error getting my requests:', error);
    return [];
  }
};

// Get request by ID
export const getRequestById = async (id) => {
  try {
    return await fetchAPI(`/requests/${id}`);
  } catch (error) {
    console.error('Error getting request by ID:', error);
    return null;
  }
};

// Create new request
export const createRequest = async (requestData) => {
  try {
    const newRequest = await fetchAPI('/requests', {
      method: 'POST',
      body: JSON.stringify(requestData),
    });
    
    // Notify approver(s) if request needs approval
    if (newRequest.status === RequestStatus.PENDING) {
      // Single approver (extensions)
      if (newRequest.approver) {
        notifyApprovalNeeded(newRequest, newRequest.approver);
      }
      
      // Multiple approvers (academic changes)
      if (newRequest.approvers && Array.isArray(newRequest.approvers)) {
        newRequest.approvers.forEach(approverEmail => {
          notifyApprovalNeeded(newRequest, approverEmail);
        });
      }
    }
    
    return newRequest;
  } catch (error) {
    console.error('Error creating request:', error);
    throw error;
  }
};

// Update request status
export const updateRequestStatus = async (id, status, updatedByEmail) => {
  try {
    const existingRequest = await getRequestById(id);
    if (!existingRequest) return null;
    
    const oldStatus = existingRequest.status;
    const updatedRequest = await fetchAPI(`/requests/${id}`, {
      method: 'PUT',
      body: JSON.stringify({
        ...existingRequest,
        status: status,
        updatedBy: updatedByEmail,
      }),
    });
    
    // Create notification for status change
    if (oldStatus !== status && updatedByEmail) {
      notifyStatusChange(updatedRequest, oldStatus, status, updatedByEmail);
    }
    
    return updatedRequest;
  } catch (error) {
    console.error('Error updating request status:', error);
    return null;
  }
};

// Update request (general purpose)
export const updateRequest = async (id, updates) => {
  try {
    return await fetchAPI(`/requests/${id}`, {
      method: 'PUT',
      body: JSON.stringify(updates),
    });
  } catch (error) {
    console.error('Error updating request:', error);
    return null;
  }
};

// Delete request
export const deleteRequest = async (id) => {
  try {
    await fetchAPI(`/requests/${id}`, {
      method: 'DELETE',
    });
    return true;
  } catch (error) {
    console.error('Error deleting request:', error);
    return false;
  }
};

// Get all requests (for approvers)
export const getAllRequests = async () => {
  try {
    const requests = await fetchAPI('/requests');

    // Sort by submittedDate (newer first)
    return requests.sort((a, b) => {
      const dateA = new Date(a.submittedDate || a.createdAt || 0);
      const dateB = new Date(b.submittedDate || b.createdAt || 0);
      return dateB - dateA;
    });
  } catch (error) {
    console.error('Error getting all requests:', error);
    return [];
  }
};

// Get statistics for dashboard
export const getRequestStats = async (userEmail) => {
  const myRequests = await getMyRequests(userEmail);
  return {
    pending: myRequests.filter(r => r.status === RequestStatus.PENDING).length,
    inProgress: myRequests.filter(r => r.status === RequestStatus.IN_PROGRESS).length,
    approved: myRequests.filter(r => r.status === RequestStatus.APPROVED).length,
    rejected: myRequests.filter(r => r.status === RequestStatus.REJECTED).length,
    completed: myRequests.filter(r => r.status === RequestStatus.COMPLETED).length,
    total: myRequests.length,
  };
};

// Add comment to request
export const addCommentToRequest = async (requestId, comment) => {
  try {
    console.log('[addCommentToRequest] Adding comment to request', requestId);
    
    // First get the current request
    const request = await getRequestById(requestId);
    if (!request) {
      throw new Error('Request not found');
    }
    
    // Use the dedicated comments endpoint
    await fetchAPI(`/requests/${requestId}/comments`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        author: comment.author,
        authorEmail: comment.authorEmail,
        content: comment.content,
        isApprover: comment.isApprover
      }),
    });
    
    // Return the updated request
    const updatedRequest = await getRequestById(requestId);
    
    console.log('[addCommentToRequest] Comment added successfully');
    return updatedRequest;
  } catch (error) {
    console.error('Error adding comment:', error);
    throw error;
  }
};